import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MAIL_UNREAD_COUNT_KEY, useMailUnreadCount } from "@/api/hooks/mail";
import { useAuthStore, usePermission } from "@/modules/auth";
import { useChannel } from "@/modules/realtime";
import { useTabBadge } from "@/shared/hooks/useTabBadge";

/** Unread conversations in your Inbox, on the browser tab ("(3) Workman"
 *  and a red count on the icon). Mail that arrives while you are in another
 *  tab makes the title alternate with "New mail (2)" until you come back.
 *  Renders nothing. */
export function MailTabBadge() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canUse = usePermission("mail.use") && user?.role !== "client";
  const unread = useMailUnreadCount(canUse);
  // New messages since you left this tab.
  const [fresh, setFresh] = useState(0);
  useChannel(canUse && user?.id ? `user.${user.id}` : null, {
    "mail.synced": (p: { newMessages?: number }) => {
      void qc.invalidateQueries({ queryKey: MAIL_UNREAD_COUNT_KEY });
      if (document.hidden && p?.newMessages) setFresh((f) => f + p.newMessages!);
    },
  });
  useEffect(() => {
    const back = () => {
      if (!document.hidden) setFresh(0);
    };
    document.addEventListener("visibilitychange", back);
    window.addEventListener("focus", back);
    return () => {
      document.removeEventListener("visibilitychange", back);
      window.removeEventListener("focus", back);
    };
  }, []);
  useTabBadge(canUse ? (unread.data ?? 0) : 0, canUse && fresh ? `New mail (${fresh}) · Workman` : null);
  return null;
}
